import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { CheckCircle2, CircleAlert } from "lucide-react";
import { toast as t, type ToastTone } from "@web/theme/theme";

export type ToastInput = { message: string; tone?: ToastTone; durationMs?: number };
type ToastItem = ToastInput & { id: number };
type Ctx = { show: (input: ToastInput | string) => void; success: (message: string) => void; error: (message: string) => void };

const ToastContext = createContext<Ctx | null>(null);

/** Mount once (main.tsx). Toasts stack above the TabBar and auto-dismiss. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const show = useCallback((input: ToastInput | string) => {
    const it: ToastItem = { id: ++seq.current, ...(typeof input === "string" ? { message: input } : input) };
    setItems((xs) => [...xs.slice(-2), it]);
    window.setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== it.id)), it.durationMs ?? 3000);
  }, []);
  const ctx = useMemo<Ctx>(() => ({ show, success: (m) => show({ message: m, tone: "success" }), error: (m) => show({ message: m, tone: "danger", durationMs: 5000 }) }), [show]);
  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <div className={t.viewport} aria-live="polite">
        {items.map((it) => (
          <div key={it.id} role="status" className={`${t.base} ${t.tone[it.tone ?? "neutral"]} ${t.offset}`}>
            {it.tone === "success" && <CheckCircle2 aria-hidden size={20} />}
            {it.tone === "danger" && <CircleAlert aria-hidden size={20} />}
            <span className="flex-1 min-w-0">{it.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): Ctx {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast() needs <ToastProvider>");
  return ctx;
}

/** Gallery-only: render a static toast without the provider. */
export function ToastPreview({ message, tone = "neutral" }: { message: string; tone?: ToastTone }) {
  return (
    <div className={`${t.base} ${t.tone[tone]}`}>
      {tone === "success" && <CheckCircle2 aria-hidden size={20} />}
      {tone === "danger" && <CircleAlert aria-hidden size={20} />}
      <span className="flex-1 min-w-0">{message}</span>
    </div>
  );
}
